import { useState, useEffect } from 'react';

import { useJobProgress, type AgentId } from '@/hooks/useJobProgress';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { postJobResume, postJobRetry } from '@/lib/api';
import type { Id } from '../../../convex/_generated/dataModel';
import { useBackendLiveness } from '@/hooks/useBackendLiveness';
import { usePublicSettings } from '@/hooks/usePublicSettings';

import { AgentSidebar } from './AgentSidebar';
import { OraPanel } from './panels/OraPanel';
import { ScryPanel } from './panels/ScryPanel';
import { LoomPanel } from './panels/LoomPanel';
import { LumenPanel } from './panels/LumenPanel';
import { MosaicPanel } from './panels/MosaicPanel';
import { ForgePanel } from './panels/ForgePanel';
import { DeliveryPanel } from './panels/DeliveryPanel';
import { PreForgeBriefDialog } from './PreForgeBriefDialog';

interface PipelineShellProps {
  jobId: string;
  projectId: Id<'projects'> | null;
}

const SELECTABLE: (AgentId | 'delivery')[] = [
  'ora', 'scry', 'loom', 'lex', 'lumen', 'mosaic', 'forge', 'delivery',
];

const RESUMABLE_FROM = new Set([
  'ora_awaiting_review',
  'scry_awaiting_review',
  'loom_awaiting_review',
  'lumen_awaiting_review',
]);

// Which agent's rows must all be approved before "Continue" is allowed.
const CHECKPOINT_AGENT: Record<string, AgentId> = {
  ora_awaiting_review: 'ora',
  scry_awaiting_review: 'scry',
  loom_awaiting_review: 'loom',
  lumen_awaiting_review: 'lumen',
};

const RUNNING_STATES = new Set([
  'created',
  'ora_running',
  'scry_running',
  'loom_running',
  'loom_lex_review',
  'lumen_running',
  'mosaic_running',
  'forge_running',
  'forge_lex_review',
]);

export function PipelineShell({ jobId, projectId }: PipelineShellProps) {
  const { token } = useAuth();
  const { toast } = useToast();
  const progress = useJobProgress(jobId as Id<'jobs'>);
  const liveness = useBackendLiveness();
  const { autoApproveCheckpoints } = usePublicSettings();
  const [selected, setSelected] = useState<AgentId | 'delivery'>('ora');
  const [resuming, setResuming] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // Auto-jump selection to whichever agent is currently active.
  useEffect(() => {
    if (!progress.state) return;
    const map: Record<string, AgentId | 'delivery'> = {
      ora_running: 'ora',
      ora_ambiguity_probe: 'ora',
      ora_awaiting_review: 'ora',
      scry_running: 'scry',
      scry_awaiting_review: 'scry',
      loom_running: 'loom',
      loom_lex_review: 'loom',
      loom_awaiting_review: 'loom',
      lumen_running: 'lumen',
      mosaic_running: 'mosaic',
      lumen_awaiting_review: 'lumen',
      // Pre-FORGE checkpoint: keep selection on the FORGE agent so the
      // upcoming-stage panel context is visible behind the modal.
      forge_awaiting_pre_input: 'forge',
      forge_running: 'forge',
      forge_lex_review: 'forge',
      completed: 'delivery',
    };
    const target = map[progress.state];
    if (target && SELECTABLE.includes(target)) {
      setSelected((cur) => (cur === target ? cur : target));
    }
  }, [progress.state]);

  if (progress.loading) return <LoadingSpinner label="Loading pipeline…" />;
  if (progress.notFound) {
    return (
      <div className="space-y-2">
        <h2 className="font-serif text-display-md italic text-ink">Job not found.</h2>
        <p className="text-graphite">The job id may be wrong, or you may not have access.</p>
      </div>
    );
  }

  const handleResume = async () => {
    if (!token) return;
    setResuming(true);
    try {
      const r = await postJobResume(token, jobId);
      toast({ title: `Job moved to ${r.new_state}.` });
    } catch (err) {
      toast({
        title: 'Resume failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setResuming(false);
    }
  };

  const handleRetry = async () => {
    if (!token) return;
    setRetrying(true);
    try {
      const r = await postJobRetry(token, jobId);
      toast({ title: `Retrying — job moved to ${r.new_state}.` });
    } catch (err) {
      toast({
        title: 'Retry failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setRetrying(false);
    }
  };

  const canResume = RESUMABLE_FROM.has(progress.state);
  const canRetry = progress.state === 'failed';

  // Rows still waiting on the participant at this checkpoint.
  const checkpointAgent = CHECKPOINT_AGENT[progress.state];
  const pendingItems = checkpointAgent
    ? progress
        .latestPerItem(checkpointAgent)
        .filter((r) => r.reviewStatus === 'awaiting_user' || r.reviewStatus === 'revising')
    : [];
  const allApproved = pendingItems.length === 0;
  const isRunning = RUNNING_STATES.has(progress.state);
  const backendDown = isRunning && liveness.loaded && !liveness.alive;

  // Per-agent "currently running" flag. Mirrors the running map in
  // AgentSidebar so the empty-state copy in each panel can read
  // "<AGENT> running…" while work is in flight.
  const runningAgents: Record<AgentId, boolean> = {
    ora: progress.state === 'ora_running' || progress.state === 'ora_ambiguity_probe',
    scry: progress.state === 'scry_running',
    loom: progress.state === 'loom_running' || progress.state === 'loom_lex_review',
    lex: progress.state === 'loom_lex_review' || progress.state === 'forge_lex_review',
    lumen: progress.state === 'lumen_running',
    mosaic: progress.state === 'mosaic_running',
    forge: progress.state === 'forge_running' || progress.state === 'forge_lex_review',
  };

  return (
    <div className="grid grid-cols-[200px_1fr] gap-10">
      <aside className="sticky top-24 self-start">
        <AgentSidebar
          jobState={progress.state}
          selected={selected}
          onSelect={setSelected}
          hasError={progress.state === 'failed'}
        />
        {canResume && (
          <div className="mt-6 space-y-2 rounded-sm border border-ochre/30 bg-ochre/5 p-3">
            <p className="smallcaps text-ochre">Awaiting your review</p>
            <p className="text-[12px] text-ink">
              {allApproved
                ? 'Everything is approved. Continue to the next stage.'
                : `${pendingItems.length} item${pendingItems.length === 1 ? '' : 's'} still need${pendingItems.length === 1 ? 's' : ''} your approval.`}
            </p>
            <Button
              variant="accent"
              size="sm"
              onClick={handleResume}
              disabled={resuming || !allApproved}
              className="w-full"
              title={allApproved ? undefined : 'Approve every item first'}
            >
              {resuming ? 'Continuing…' : 'Continue'}
            </Button>
          </div>
        )}
        {progress.statusLine && progress.state !== 'completed' ? (
          <p className="mt-4 font-mono text-[11px] leading-relaxed text-graphite">
            <span className="text-ochre">¶</span>&nbsp;&nbsp;{progress.statusLine}
          </p>
        ) : null}
        {autoApproveCheckpoints && !canResume ? (
          <p className="mt-3 font-mono text-[11px] leading-relaxed text-graphite">
            Checkpoints are auto-approved for this study session.
          </p>
        ) : null}
      </aside>

      <main className="min-w-0">
        {backendDown && (
          <div className="mb-6 rounded-sm border border-ochre/40 bg-ochre/5 p-4">
            <p className="smallcaps text-ochre">
              {liveness.paused ? 'Pipeline paused' : 'Waiting for the AURA backend'}
            </p>
            <p className="mt-1 text-body text-ink">
              {liveness.paused
                ? 'The study team has paused the agents. Your job is queued and will resume automatically.'
                : 'The agent runner has not reported in for a while. Your job is safely queued and will continue as soon as it is back — nothing to do on your side.'}
            </p>
          </div>
        )}
        {progress.state === 'failed' && (
          <div className="mb-6 rounded-sm border border-vermilion/40 bg-vermilion/5 p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="smallcaps text-vermilion">Pipeline failed</p>
                <p className="mt-1 text-body text-ink">{progress.error || 'Unknown error.'}</p>
              </div>
              {canRetry && (
                <Button
                  variant="accent"
                  size="sm"
                  onClick={handleRetry}
                  disabled={retrying}
                >
                  {retrying ? 'Retrying…' : 'Retry from failed stage'}
                </Button>
              )}
            </div>
          </div>
        )}

        {selected === 'ora' && (
          <OraPanel
            jobId={jobId}
            output={progress.latestFor('ora')}
            isRunning={runningAgents.ora}
          />
        )}
        {selected === 'scry' && (
          <ScryPanel
            jobId={jobId}
            projectId={projectId}
            pages={progress.latestPerItem('scry')}
            isRunning={runningAgents.scry}
          />
        )}
        {selected === 'loom' && (
          <LoomPanel
            jobId={jobId}
            output={progress.latestFor('loom')}
            isRunning={runningAgents.loom}
          />
        )}
        {selected === 'lex' && (
          <LexSummary
            descriptorReviews={progress.outputs.filter(
              (o) => o.agent === 'lex' && o.itemKey?.startsWith('loom_iter_'),
            )}
            codeReviews={progress.outputs.filter(
              (o) => o.agent === 'lex' && o.itemKey?.startsWith('forge_batch_'),
            )}
          />
        )}
        {selected === 'lumen' && (
          <LumenPanel
            jobId={jobId}
            outputs={progress.latestPerItem('lumen')}
            artifacts={progress.artifacts}
            isRunning={runningAgents.lumen}
          />
        )}
        {selected === 'mosaic' && (
          <MosaicPanel
            outputs={progress.latestPerItem('mosaic')}
            artifacts={progress.artifacts}
            isRunning={runningAgents.mosaic}
          />
        )}
        {selected === 'forge' && (
          <ForgePanel
            forgeOutputs={progress.outputs.filter((o) => o.agent === 'forge')}
            lexOutputs={progress.outputs.filter((o) => o.agent === 'lex')}
            isRunning={runningAgents.forge}
          />
        )}
        {selected === 'delivery' && (
          <DeliveryPanel artifacts={progress.artifacts} />
        )}
      </main>

      {/* Pre-FORGE checkpoint modal. Mounted at the shell level so it
          appears regardless of which agent panel is currently selected. */}
      <PreForgeBriefDialog
        open={progress.state === 'forge_awaiting_pre_input'}
        jobId={jobId as Id<'jobs'>}
      />
    </div>
  );
}

// LEX summary view — surfaces all reviews across both modes for transparency.
function LexSummary({
  descriptorReviews,
  codeReviews,
}: {
  descriptorReviews: { _id: string; payload: unknown; itemKey: string | null }[];
  codeReviews: { _id: string; payload: unknown; itemKey: string | null }[];
}) {
  return (
    <section className="space-y-6">
      <header>
        <h2 className="font-serif text-display-sm italic text-ink">LEX — Validator</h2>
        <p className="mt-1 text-body text-graphite">
          The agentic guardrail. LEX critiques LOOM's descriptor and FORGE's code; reviews are
          surfaced here for inspection but require no checkpoint action.
        </p>
      </header>

      <div className="border-t border-ink/10 pt-6 space-y-6">
        {descriptorReviews.length > 0 && (
          <div>
            <p className="smallcaps text-graphite mb-2">Descriptor reviews</p>
            <ul className="space-y-1">
              {descriptorReviews.map((r) => {
                const p = r.payload as { pass?: boolean; violations?: { id: string; severity: string }[] };
                return (
                  <li key={r._id} className="font-mono text-[12px] text-ink">
                    {r.itemKey} — {p.pass ? '✓ pass' : `${p.violations?.length ?? 0} issues`}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {codeReviews.length > 0 && (
          <div>
            <p className="smallcaps text-graphite mb-2">Code reviews</p>
            <ul className="space-y-1">
              {codeReviews.map((r) => {
                const p = r.payload as { pass?: boolean; violations?: { id: string; severity: string }[] };
                return (
                  <li key={r._id} className="font-mono text-[12px] text-ink">
                    {r.itemKey} — {p.pass ? '✓ pass' : `${p.violations?.length ?? 0} issues`}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {descriptorReviews.length === 0 && codeReviews.length === 0 && (
          <p className="text-graphite">No LEX reviews recorded yet.</p>
        )}
      </div>
    </section>
  );
}
